import React, {
    FormEvent,
    PropsWithChildren,
    ReactElement,
    useEffect,
    useState,
} from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import SessionStorage, { clearAquisitionData } from '../../utils/SessionStorage';
import {
    wrapTrademarkSuperscript,
} from '../../utils/cob-utils';
import {
    acquisitionApiRequest,
    AcquisitionInitResponse,
} from '../../utils/acquisition-api/AcquisitionApi';
import { useAcquisitionContext } from '../../providers/AcquisitionProvider/AcquisitionProvider';
import {
    AcquisitionFlow,
    getAcquisitionFlow,
} from '../../utils/acquisition-utils';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { useLoader } from '../../providers/Loader/LoaderProvider';

import axios from 'axios';

import './ApplicationCta.css';
import { useSessionStorageStatus } from '../../providers/SessionStorageStatus/SessionStorageStatusProvider';
import useSessionStorageCheck from '../../hooks/useSessionStorageCheck';
import { Button } from '../../importCL';
import AcquisitionErrorToggleEvent from './AcquisitionErrorToggleEvent';
import { addressValidationSemaphore } from '../PqDataEntryForm/util/addressValidationSemaphore';
import useAcquisitionProviderFacade from '../../facades/AcquisitionProviderFacade/useAcquisitionProviderFacade';
import useHandleErrorPushdown from './useHandleErrorPushdown-Refactored';
import useApplicationCtaFormValidations, { ApplicationCtaValidationError } from './FormValidations/useApplicationCtaFormValidations';
import useApplicationSubmit from './ApplicationSubmit/useApplicationSubmit';
import { useHandleErrorPushup } from './useHandleErrorPushup';
import { usePushdownContext } from '../../providers/Pushdown/PushdownProvider';

export interface IApplicationCtaProps extends MappedComponentProperties {
    ctaText?: string;
    ctaDisclaimerText?: string;
    contentFragmentFolder?: string;
    defaultRedirectUrl?: string;
    dataEntryUrl?: string;
    serverErrorCode?: string;
    serverSessionExpiredErrorCode?: string;
    fundingToggleRedirect?: string;
    splitApplication?: boolean;
    firstPage?: boolean;
    lastEntryPage?: boolean;
}

interface IDisclaimerContent {
    disclaimerText?: string;
}

export const ApplicationCtaEditConfig = {
    emptyLabel: 'Application Cta',
    isEmpty: (props: PropsWithChildren<IApplicationCtaProps>) => {
        return !props || !props.ctaText;
    },
};

const ApplicationCta = (
    props: PropsWithChildren<IApplicationCtaProps>,
): JSX.Element => {
    const { status } = useSessionStorageStatus();
    useSessionStorageCheck();
    const [dynamicDisclaimer, setDynamicDisclaimer] = useState<string>(PathUtils.getMetaPropertyValue(
            'SkipPqDisclosureContent',
        ));
    const { setShouldRenderLoader } = useLoader();

    const {
        canFullAppContinue,
        isApplicationInfoRecieved,
        isTermsValid,
    } = useAcquisitionContext();

    const acquisitionProviderFacade = useAcquisitionProviderFacade();

    const [isButtonAwaitingResponse, setIsButtonAwaitingResponse] =
        useState<boolean>(false);

    const { showPushdown } = usePushdownContext();

    const { 
        checkPassiveValidation,
        performActiveValidations
    } = useApplicationCtaFormValidations();

    const { 
        persistData,
        applicationSubmit,

    } = useApplicationSubmit(
        props,
        setShouldRenderLoader,
        setIsButtonAwaitingResponse
    );
    const { handleErrorPushdown } = useHandleErrorPushdown();
    const { InlineErrorMessages } = useHandleErrorPushup();

    const handleSubmit = async (e: FormEvent<HTMLButtonElement>): Promise<void> => {
        e.preventDefault();
        setShouldRenderLoader(true);

        // TODO: Eliminate Semaphore, shouldn't be necessary with functional approach
        addressValidationSemaphore.acquire();

        const validatingState = acquisitionProviderFacade;
        await checkPassiveValidation(validatingState)
            .then(performActiveValidations)
            .then(async (validatingState) => {
                await persistData(validatingState);
                if (props.lastEntryPage) {
                    applicationSubmit(validatingState)
                } else {
                    window.location.assign(`${props.defaultRedirectUrl}${window.location.search}`);
                }
            })
            .catch(async (error: unknown) => {
                if (error instanceof ApplicationCtaValidationError) {
                    if (error.isApplicationAborted) {
                        clearAquisitionData();

                        const params = new URLSearchParams(window.location.search); 
                        params.set('errorCode', 'processingError');

                        window.location.assign(`${props.dataEntryUrl}?${params.toString()}`);

                        return;
                    } else if (error.cause.includes('API Call Failure')) {
                        showPushdown(props.serverErrorCode);
                        setShouldRenderLoader(false);
                        setIsButtonAwaitingResponse(false);
                        return;
                    }
                }


                const toDispatch = acquisitionProviderFacade.getPersonalInfoForm();

                window.dispatchEvent( new AcquisitionErrorToggleEvent(toDispatch) );

                // Performs Field Validations, creating pushdown messages
                await handleErrorPushdown(validatingState);

                setIsButtonAwaitingResponse(false);
                setShouldRenderLoader(false);
                addressValidationSemaphore.release();
            });

        addressValidationSemaphore.release();
    };

    useEffect(() => {
        if (
            isApplicationInfoRecieved
            && props.contentFragmentFolder
            && SessionStorage.verify('selectedProgramCode')
        ) {
            const servlet = '/services/v2/contentFragmentServlet';
            const path = props.contentFragmentFolder;
            const programCode = SessionStorage.get('selectedProgramCode');
            const filters = JSON.stringify({ programCodes: programCode });

            axios
                .get<any, { data: IDisclaimerContent[] }>(
                    servlet +
                    '.path=' +
                    path +
                    '.filters=' +
                    filters +
                    '.optional=true',
                )
                .then((result: { data: IDisclaimerContent[] }) => {
                    if (result.data[0].disclaimerText) {
                        setDynamicDisclaimer(result.data[0].disclaimerText);
                    } else {
                        throw { response: result };
                    }
                })
                .catch(() => { });
        }
    }, [isApplicationInfoRecieved]);

    return (
        <div id='application-cta' className='ApplicationCta'>
            {props.ctaDisclaimerText && (
                <div className='ApplicationCta__disclaimer'>
                    {transformHTMLParserLinks(props.ctaDisclaimerText)}
                </div>
            )}
            <InlineErrorMessages />
            <Button
                className='ApplicationCta__cta'
                onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) =>
                    e.preventDefault()
                }
                onMouseUp={(e: any) => e.target.focus()}
                onClick={(e: FormEvent<HTMLButtonElement>) => {
                    if (
                        getAcquisitionFlow() === AcquisitionFlow.SkipPQ &&
                        !SessionStorage.verifyNotEmpty('initResponse')
                    ) {
                        acquisitionApiRequest<AcquisitionInitResponse>(
                            'init-compare-api',
                            {},
                        ).then(async response => {
                            SessionStorage.setObject(
                                'initResponse',
                                response.data,
                            );
                            handleSubmit(e);
                        });
                        return;
                    }
                    handleSubmit(e);
                }}
                id='full-app-continue'
                size='medium'
                target='_self'
                variant='solid'
                disabled={(
                    (!props.splitApplication && (!canFullAppContinue || !isTermsValid))
                    || (props.splitApplication && !props.firstPage && (!canFullAppContinue || !isTermsValid))
                    || isButtonAwaitingResponse
                    || status === 'unusable'
                )}
            >
                {
                    transformHTMLParserLinks(
                        wrapTrademarkSuperscript(props.ctaText),
                    ) as unknown as ReactElement
                }
            </Button>
            {dynamicDisclaimer && (
                <div className='ApplicationCta__dynamicDisclaimer'>
                    {transformHTMLParserLinks(dynamicDisclaimer)}
                </div>
            )}
        </div>
    );
};

export default ApplicationCta;
